import { getISOWeek, getWeek } from "date-fns";
import { convertToLocalTime } from "date-fns-timezone";
import {
  BreakdownWithTagsType,
  PnlBreakdown,
} from "reports/getters/pnlBreakdownGetter/pnlBreakdownTypes";
import { round } from "lodash";
import {
  DOWNTIME_PNL_CATEGORIES,
  PnlCategoriesEnum,
  PnlCategoryLabelsEnum,
} from "reports/types";
import {
  getSummaryMetrics,
  SummaryMetricInput,
} from "reports/utils/metricUtils";
import { MetricEnum } from "reports/utils/metricTypes";
import {
  getRoundedMinutesMs,
  ONE_HOUR_OF_MS,
  ONE_MINUTE_OF_MS,
  FORMAT_MONTH,
} from "reports/utils/timeUtils";
import { SummaryMetricType } from "../SummaryMetricsRow";
import { format } from "date-fns";
import { StackedPnlBarChartDataType } from "./Charts/StackedPnLBarChart";
import { TFunction } from "i18next";

export const CHART_HEIGHT = 500;
export const CHART_WIDTH = 1000;
export const RECHARTS_FULL_WIDTH = 1700;

/** Sets the hours and minutes of the given Date in tenant's timezone and returns the epoch time
 *
 * convertToLocalTime Doc : https://github.com/prantlf/date-fns-timezone/blob/master/docs/API.md#converttolocaltime
 */
export const getEpochTime = ({
  hour = 0,
  minute = 0,
  seconds = 0,
  timeZone,
  baselineDate = new Date(),
}: {
  hour?: number;
  minute?: number;
  seconds?: number;
  timeZone: string;
  baselineDate?: Date;
}): number => {
  const dateObj = baselineDate.setHours(hour, minute, seconds, 0);
  return convertToLocalTime(dateObj, { timeZone }).getTime();
};

/**
 * Format pnlBreakddown to data format for a volume chart
 *
 * @param {PnlBreakdown}
 * @return {*}
 */
export const pnlBreakdownToDownTimesChartData = ({
  pnlBreakdown,
  categoriesToSkip = [],
  tagsToSkip = [],
  showMinutes = false, // Set to True to show the duration of the downtime in minutes (hours by default).
  numberOfDowntimes,
}: {
  pnlBreakdown: PnlBreakdown;
  categoriesToSkip?: PnlCategoriesEnum[];
  tagsToSkip?: string[];
  showMinutes?: boolean;
  numberOfDowntimes?: number;
}) => {
  let downtimesByTag = [] as {
    category: PnlCategoriesEnum;
    name: string;
    duration: number;
    percent: number;
    frequency: number;
  }[];

  const categoriesToShow = DOWNTIME_PNL_CATEGORIES.filter(
    item => !categoriesToSkip.includes(item)
  );

  // Gather all downtime tags per category
  categoriesToShow.forEach(category => {
    const pnlBreakdownForCategory = pnlBreakdown?.[
      category
    ] as BreakdownWithTagsType;
    const tagDowntimes =
      pnlBreakdownForCategory?.tags
        ?.filter(({ tagName }) => !tagsToSkip.includes(tagName))
        .map(({ tagName, durationMs, numberOfEvents }) => {
          return {
            category,
            name: tagName,
            // Normally we would round this value, however, we use `getRoundedMinutesMs` to shift the value to the rounded minute.
            // Rounding this value further will lead to off-by-1-minute errors.
            duration:
              getRoundedMinutesMs(durationMs) /
              (showMinutes ? ONE_MINUTE_OF_MS : ONE_HOUR_OF_MS),
            percent: pnlBreakdown?.productionTime
              ? round((durationMs / pnlBreakdown.productionTime) * 100, 1)
              : 0,
            frequency: numberOfEvents,
          };
        }) ?? [];
    downtimesByTag = [...downtimesByTag, ...tagDowntimes];
  });

  // Add unknown time to downtimes
  downtimesByTag = categoriesToSkip.includes(PnlCategoriesEnum.unknown)
    ? downtimesByTag
    : [
        ...downtimesByTag,
        {
          category: PnlCategoriesEnum.unknown,
          name: PnlCategoryLabelsEnum.unknown,
          duration: round(
            (pnlBreakdown?.unknown?.durationMs ?? 0) /
              (showMinutes ? ONE_MINUTE_OF_MS : ONE_HOUR_OF_MS),
            1
          ),
          percent: pnlBreakdown?.productionTime
            ? round(
                ((pnlBreakdown?.unknown?.durationMs ?? 0) /
                  pnlBreakdown.productionTime) *
                  100,
                1
              )
            : 0,
          frequency: pnlBreakdown?.unknown?.numberOfEvents ?? 0,
        },
      ];

  const sortedTagsByDowntime = [...downtimesByTag].sort(
    (a, b) => b.percent - a.percent
  );
  const topXDowntimes = numberOfDowntimes
    ? sortedTagsByDowntime.slice(0, numberOfDowntimes)
    : sortedTagsByDowntime;

  return {
    chartData: topXDowntimes,
    downTimesToShow: topXDowntimes.filter(({ percent }) => percent >= 1),
    downTimesToHide: topXDowntimes.filter(({ percent }) => percent < 1),
  };
};

/**
 * Format pnlBreakddowns to data format for a stacked bar chart
 *
 * @param {PnlBreakdown[]}
 * @return {*}
 */
export const pnlBreakdownsToProductionPnlTimesChartData = ({
  t,
  pnlBreakdowns,
  chartDataType = "W",
  metricTitle,
  shouldUseISO = false,
}: {
  t: TFunction;
  pnlBreakdowns: PnlBreakdown[];
  chartDataType?: "W" | "D" | "M";
  metricTitle: MetricEnum;
  shouldUseISO?: boolean;
}): {
  chartData: StackedPnlBarChartDataType[];
  metricDetails: SummaryMetricType | null;
} => {
  const pnlBreakdownsSorted = [...pnlBreakdowns].sort((a, b) => a.ge - b.ge);
  let metricDetails = null as SummaryMetricType | null;

  const chartData = pnlBreakdownsSorted.map(data => {
    const date =
      chartDataType === "M"
        ? format(data.ge, FORMAT_MONTH)
        : `${t("W")} - ${
            shouldUseISO
              ? getISOWeek(Number(data.ge))
              : getWeek(Number(data.ge))
          }`;

    const scale = chartDataType === "M" ? -1 : 1;
    const summaryMetrics = getSummaryMetrics({
      metrics: [
        { name: MetricEnum.PRODUCING_PERCENT },
        { name: MetricEnum.PLANNED_DOWNTIME_PERCENT },
        { name: MetricEnum.UNPLANNED_DOWNTIME_PERCENT },
        { name: MetricEnum.WAITING_DOWNTIME_PERCENT },
        { name: MetricEnum.UNKNOWN_DOWNTIME_PERCENT },
        { name: metricTitle },
        { name: MetricEnum.SPEED_LOSS_PERCENT },
      ],
      pnlBreakdown: data,
      t,
    });

    const summaryMetricItem = summaryMetrics?.find(
      ({ name }) => name === t(metricTitle)
    );
    metricDetails = summaryMetricItem ? summaryMetricItem : null;

    return {
      date,
      [PnlCategoriesEnum.producing]: getValueFromSummaryMetrics(
        t(MetricEnum.PRODUCING_PERCENT),
        summaryMetrics
      ),
      [PnlCategoriesEnum.planned]: getValueFromSummaryMetrics(
        t(MetricEnum.PLANNED_DOWNTIME_PERCENT),
        summaryMetrics
      ),
      [PnlCategoriesEnum.waiting]:
        getValueFromSummaryMetrics(
          t(MetricEnum.WAITING_DOWNTIME_PERCENT),
          summaryMetrics
        ) * scale,
      [PnlCategoriesEnum.unplanned]:
        getValueFromSummaryMetrics(
          t(MetricEnum.UNPLANNED_DOWNTIME_PERCENT),
          summaryMetrics
        ) * scale,
      [PnlCategoriesEnum.unknown]:
        getValueFromSummaryMetrics(
          t(MetricEnum.UNKNOWN_DOWNTIME_PERCENT),
          summaryMetrics
        ) * scale,
      [PnlCategoriesEnum.notScheduled]: 0,
      metric: getValueFromSummaryMetrics(t(metricTitle), summaryMetrics),
      speedLoss: getValueFromSummaryMetrics(
        MetricEnum.SPEED_LOSS_PERCENT,
        summaryMetrics
      ),
    };
  });

  return { chartData, metricDetails };
};

const getValueFromSummaryMetrics = (
  metricName: string,
  metricList: SummaryMetricType[]
) => {
  return round(
    metricList.find(m => m.name === metricName)?.numericValue || 0,
    1
  );
};

/**
 * Format pnlBreakddown to data format for last N weeks metrics(OEE,TEEP etc) chart
 *
 * @param {PnlBreakdown[]}
 * @return {*}
 */
export function pnlBreakdownToMetricChartData({
  summaryMetricInputList,
  pnlBreakdowns,
  t,
  descendingOrder = true,
  shouldUseISO = false,
}: {
  summaryMetricInputList: SummaryMetricInput[];
  pnlBreakdowns: PnlBreakdown[];
  t: TFunction;
  descendingOrder?: boolean;
  shouldUseISO?: boolean;
}) {
  const pnlBreakdownsSorted = [...pnlBreakdowns].sort((a, b) =>
    descendingOrder ? b.ge - a.ge : a.ge - b.ge
  );

  const metricDetails = getSummaryMetrics({
    metrics: summaryMetricInputList,
    pnlBreakdown: pnlBreakdownsSorted[0],
    t,
  });

  const chartData = pnlBreakdownsSorted.map(data => {
    const geToWeekNumber = `${t("W")} - ${
      shouldUseISO ? getISOWeek(Number(data.ge)) : getWeek(Number(data.ge))
    }`;

    const summaryMetrics = getSummaryMetrics({
      metrics: summaryMetricInputList,
      pnlBreakdown: data,
      t,
    });

    const metricTrend = { date: geToWeekNumber };

    summaryMetrics.forEach(metric => {
      metricTrend[metric.name] = metric.numericValue ?? 0;
      metricTrend[`${metric.name}_unit`] = metric.unit;
    });

    return metricTrend;
  });
  return { chartData, metricDetails };
}
